import type { ReactNodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { Resizable } from 're-resizable'
import type { FC } from 'react'
import React, { useRef } from 'react'
import styled from 'styled-components'

import { getImageFullUrlInApplication } from '../../../utils'

const Container = styled(NodeViewWrapper)`
    position: relative;
    vertical-align: middle;
`

const TopLeftHandle = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`

const TopRightHandle = styled.div`
    position: absolute;
    z-index: 1;
    right: 0;
    top: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`

const BottomLeftHandle = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    bottom: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`

const BottomRightHandle = styled.div`
    position: absolute;
    z-index: 1;
    right: 0;
    bottom: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`

const StyledVideo = styled.video`
    width: 100%;
    display: block;
    border-radius: 4px;
`

export const ResizeableVideo: FC<ReactNodeViewProps> = ({ node, selected, updateAttributes, editor }) => {
    const { attrs } = node
    const videoExt = !editor.isDestroyed ? editor.extensionManager?.extensions.find(ext => ext.name === 'video') : undefined
    const { options } = videoExt ?? { options: {} }
    const { appId } = options

    const containerRef = useRef<HTMLDivElement | null>(null)
    const ref = useRef<HTMLVideoElement | null>(null)

    const beforeResizingWidth = useRef(0)

    // 判断是否是外部链接
    const isExternalUrl = (url: string) => {
        return url?.startsWith('http://') || url?.startsWith('https://')
    }

    const videoSrc = isExternalUrl(attrs.src) ? attrs.src : getImageFullUrlInApplication(appId, attrs.src)

    return (
        <Container ref={containerRef} style={{ textAlign: attrs.textAlign }}>
            <Resizable
                enable={editor.isEditable ? undefined : false}
                style={{ display: 'inline-block' }}
                maxWidth="100%"
                lockAspectRatio
                size={attrs.width ? { width: attrs.width } : undefined}
                onResizeStart={() => {
                    if (ref.current) {
                        beforeResizingWidth.current = ref.current.clientWidth
                    }
                }}
                onResize={(e, _, __, d) => {
                    if (e.target instanceof HTMLElement) {
                        const width = beforeResizingWidth.current + d.width

                        const containerEl = containerRef.current
                        if (!containerEl) {
                            return
                        }

                        const ratio = `${(width / containerEl.clientWidth) * 100}%`
                        updateAttributes({ ...attrs, width: ratio })
                    }
                }}
                onResizeStop={() => {
                    beforeResizingWidth.current = 0
                }}
            >
                {selected && editor.isEditable && (
                    <>
                        <TopLeftHandle />
                        <TopRightHandle />
                        <BottomLeftHandle />
                        <BottomRightHandle />
                    </>
                )}
                <StyledVideo ref={ref} className={selected ? 'ProseMirror-selectednode' : ''} src={videoSrc} controls={attrs.controls} />
            </Resizable>
        </Container>
    )
}
